'use client';

import type { User } from '@/types/user/type';
import { VpDivider, VpFormControl, VpFormHelper, VpFormLabel, VpInput, VpSticker } from '@vtmn-play/react/headless';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { fetchItem } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { Author } from '~/components/commons/Author/Author';
import { UserPageHeader } from '~/components/commons/UserPage/UserPageHeader';
import { Toaster } from '~/components/Profile/Toaster';
import { isUserAdmin } from '~/components/Profile/utils';
import { apiTypes } from '~/const/api';
import { AcceptLanguage } from '~/const/locale';
import { useRequestState } from '~/hooks/request/useRequestState';
import { getFormattedAnswerValue } from '~/utils/utils';
import { Submit } from '../../profile/components/Submit';

export const AdministratorData = () => {
  const t = useTranslations('SETTINGS.ADMINISTRATOR_DATA');
  const locale = useLocale();

  const { user, setUser } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();

  const [dataUpdated, setDataUpdated] = useState(false);
  const [userUpdatingData, setUserUpdatingData] = useState(user);

  const { startRequest, handleSuccess, handleError, status } = useRequestState();

  const isPending = status === 'pending';

  const canBeSubmitted = user?.label !== userUpdatingData?.label;

  const updateUserDataInState = (keyAttribute, e) => {
    const newUserUpdatingData = {
      ...userUpdatingData,
      [keyAttribute]: getFormattedAnswerValue(e.target.value, apiTypes.TEXT),
    };
    setUserUpdatingData(newUserUpdatingData);
  };

  const handleUserPutData = async () => {
    startRequest();

    const response = await fetchItem<User>({
      pathname: `users/me`,
      method: 'PUT',
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      body: { label: userUpdatingData.label || null },
    });

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    setUser(response);
    handleSuccess();
    setDataUpdated(true);
    setTimeout(() => setDataUpdated(false), 3000);
  };

  return (
    user
    && isUserAdmin(user) && (
      <div
        className="administrator-data"
        data-testid="administrator-data"
      >
        <UserPageHeader
          title={<span>{t('TITLE')}</span>}
          description={<span>{t('SUBTITLE')}</span>}
        />
        <VpDivider />
        <div className="administrator-data__preview">
          <h2 className="vp-subtitle-m">
            {t('CONTENT')}
          </h2>
          <div className="administrator-data__preview__content">
            <div>
              <Author user={user} />
            </div>
            <VpSticker variant="secondary" size="small" data-testid="sticker">
              {user.label ?? user.job}
            </VpSticker>
          </div>
        </div>
        <VpDivider />
        <div className="profile__fieldgroup">
          <div className="profile__field">
            <VpFormControl>
              <VpFormLabel>{t('INPUT_LABEL')}</VpFormLabel>
              <VpInput
                id="job"
                data-testid="label"
                defaultValue={userUpdatingData?.label}
                onChange={e => updateUserDataInState('label', e)}
                placeholder={user?.job}
              />
              <VpFormHelper>{t('INPUT_HELPER')}</VpFormHelper>
            </VpFormControl>
          </div>
        </div>
        <div>
          <Submit
            user={user as User}
            handleUserPutData={handleUserPutData}
            canBeSubmitted={canBeSubmitted}
            isLoading={isPending}
            setUserUpdatingData={setUserUpdatingData}
          />
        </div>
        {dataUpdated && <Toaster />}
      </div>
    )
  );
};
